import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

/**
 * Minimal user shape required by the avatar component.
 * Intentionally separate from the API User type — only needs display fields.
 */
export type AvatarUserType = {
  readonly id: number;
  readonly name: string;
  readonly image?: string;
  [key: string]: any;
};

/**
 * Displays a stack of user avatar images with initials fallback.
 * Shows a +N indicator when users exceed the display limit.
 */
@Component({
  selector: 'app-user-avatar',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex items-center {{ className() }}">
      @for (user of visibleUsers(); track user.id; let first = $first) {
        <div
          class="avatar-container"
          [ngClass]="{ '-ml-2': !first, '!bg-white': user.image && !hasImageError(user.id) }"
          [title]="allowTooltip() ? user.name : ''"
        >
          @if (user.image && !hasImageError(user.id)) {
            <img
              [src]="retryImageUrl(user)"
              [alt]="user.name"
              class="h-full w-full rounded-full object-cover"
              (error)="handleError(user.id)"
            />
          } @else {
            <span
              class="avatar-text text-[29.5px] not-italic leading-[normal] tracking-[normal] text-center text-white"
              >{{ getInitials(user.name) }}</span
            >
          }
        </div>
      }

      @if (remainingCount() > 0) {
        <div
          class="avatar-container -ml-2"
          [title]="allowTooltip() ? getRemainingUsersTooltip() : ''"
        >
          <span class="avatar-text text-xs">+{{ remainingCount() }}</span>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: contents;
    }

    .avatar-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      color: #ffffff;
      overflow: hidden;
      @apply bg-dark;
    }

    .avatar-container:hover {
      z-index: 10;
    }

    .avatar-text {
      font-family: inherit;
      font-size: 0.875rem;
      font-weight: 500;
      line-height: 1.75rem;
      margin: 0;
      padding: 0;
      display: block;
      text-align: center;
      width: 100%;
    }
  `,
})
export class UserAvatarComponent {
  /** List of users to display as avatars */
  public users = input<AvatarUserType[]>([]);

  /** Maximum number of visible avatars before +N indicator */
  public displayLimit = input<number>(2);

  /** Whether to show user name on hover */
  public allowTooltip = input<boolean>(false);

  /** Additional CSS classes for the container */
  public className = input<string>('');

  /** Tracks image load failure counts per user ID */
  private readonly errorCounts = signal<Record<number, number>>({});

  /** Users shown as visible avatars */
  protected readonly visibleUsers = computed(() => this.users().slice(0, this.displayLimit()));

  /** Count of users beyond the display limit */
  protected readonly remainingCount = computed(() =>
    Math.max(0, this.users().length - this.displayLimit()),
  );

  /** Extracts initials from a full name */
  protected getInitials(name: string): string {
    if (!name) return '';

    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }

    const firstInitial = parts[0].charAt(0).toUpperCase();
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return firstInitial + lastInitial;
  }

  /** Builds tooltip text for overflow users */
  protected getRemainingUsersTooltip(): string {
    return this.users()
      .slice(this.displayLimit())
      .map((u) => u.name)
      .join(', ');
  }

  /** Checks if image has exceeded retry limit */
  protected hasImageError(userId: number): boolean {
    return (this.errorCounts()[userId] || 0) >= 3;
  }

  /** Appends retry query param to force image reload */
  protected retryImageUrl(user: AvatarUserType): string {
    const count = this.errorCounts()[user.id] || 0;
    if (count > 0 && user.image) {
      const separator = user.image.includes('?') ? '&' : '?';
      return `${user.image}${separator}retry=${count}`;
    }
    return user.image || '';
  }

  /** Increments error count on image load failure */
  protected handleError(userId: number): void {
    const currentCounts = this.errorCounts();
    const newCount = (currentCounts[userId] || 0) + 1;

    this.errorCounts.set({
      ...currentCounts,
      [userId]: newCount,
    });
  }
}
